import React, { useState } from 'react';
import { format } from 'date-fns';
import { CalendarIcon, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * Button that opens a calendar to pick a single date (no future dates).
 * @param {string} label - Placeholder shown when empty, e.g. "From"
 * @param {string|null} value - Selected date as 'yyyy-MM-dd'
 * @param {(value: string|null) => void} onChange - Called with 'yyyy-MM-dd' or null when cleared
 * @param {string|null} minDate - Earliest selectable date ('yyyy-MM-dd')
 * @param {string|null} maxDate - Latest selectable date ('yyyy-MM-dd')
 */
const DatePickerField = ({ label, value, onChange, minDate, maxDate, title, className }) => {
    const [open, setOpen] = useState(false);

    const select = (dateStr) => {
        onChange(dateStr);
        setOpen(false);
    };

    const isDisabled = (date) => {
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        if (date > today) return true;
        if (minDate && date < new Date(minDate)) return true;
        if (maxDate && date > new Date(maxDate)) return true;
        return false;
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    title={title}
                    className={cn("h-8 gap-1.5 px-2 min-w-[120px] justify-start", className)}
                >
                    <CalendarIcon className="h-3 w-3 text-muted-foreground" />
                    {value ? (
                        <span className="truncate text-xs">{format(new Date(value), 'MMM d, yyyy')}</span>
                    ) : (
                        <span className="text-muted-foreground text-xs">{label}</span>
                    )}
                    <ChevronDown className="h-3 w-3 ml-auto opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
                <div className="p-2 border-b">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start text-xs text-muted-foreground"
                        onClick={() => select(null)}
                    >
                        Clear {label.toLowerCase()} date
                    </Button>
                </div>
                <Calendar
                    mode="single"
                    selected={value ? new Date(value) : undefined}
                    onSelect={(date) => select(date ? format(date, 'yyyy-MM-dd') : null)}
                    disabled={isDisabled}
                />
            </PopoverContent>
        </Popover>
    );
};

export default DatePickerField;
